<template>
  <div class="call-list-filter">
    <div class="row">
      <div>
        <BDateRangeForm
          v-model="startedAtRange"
          type="datetimerange"
          clearable
        />
      </div>
      <div class="caller-user-select">
        <SelectUser
          :key="selectUserInputRenderKey"
          v-model="selectUserInputValue"
          :use-all-label="$t('vaApp.callList.filter.allCallers')"
          flexible-width
          :show-overlay-loading-indicator="false"
          :options-filter="optionsFilter"
        />
      </div>
      <div class="callee-did-number-input">
        <BInput
          v-model="calleeDidNumberInputValue"
          :placeholder="$t('vaApp.callList.filter.calleeDidNumber.placeholder')"
          clearable
          @blur="handleCalleeDidNumberInputBlur"
          @keypress-enter="handleCalleeDidNumberInputKeypressEnter"
        >
          <template #prefix>
            <span class="callee-did-number-input-icon">
              <BIcon>search</BIcon>
            </span>
          </template>
        </BInput>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineModel, computed, ref, onMounted } from 'vue';
import { UserForDisplay, User } from '@/api/openapi';
import { ZoomPhoneResultApiService } from '@/api/user/resources/zoom_phone_result';
import { useCurrentUserProvider } from '@/composable/current-user';
import { formatDateTimeWithoutSecond, subtract, startOf, endOf } from '@/utils/date-time';

const startedAtFrom = defineModel<string | undefined>('startedAtFrom');
const startedAtTo = defineModel<string | undefined>('startedAtTo');
const callerUserId = defineModel<string | undefined>('callerUserId');
const calleeDidNumber = defineModel<string | undefined>('calleeDidNumber');

const callers = ref<UserForDisplay[]>([]);
const selectUserInputRenderKey = ref<number>(0);
const calleeDidNumberInputValue = ref<string>(calleeDidNumber.value || '');

const { inject: injectCurrentUser } = useCurrentUserProvider();
const { currentUser } = injectCurrentUser();

const startedAtRange = computed<Date[]>({
  get() {
    return [new Date(startedAtFrom.value), new Date(startedAtTo.value)];
  },
  set(value: Date[]) {
    startedAtFrom.value = formatDateTimeWithoutSecond(value[0]) || 'all';
    startedAtTo.value = formatDateTimeWithoutSecond(value[1]) || 'all';
  },
});

const selectUserInputValue = computed({
  get() {
    return callerUserId.value === 'all' ? undefined : Number(callerUserId.value);
  },
  set(value: string | undefined) {
    callerUserId.value = value?.toString() || 'all';
  },
});

const handleCalleeDidNumberInputBlur = () => {
  calleeDidNumber.value = calleeDidNumberInputValue.value;
};

const handleCalleeDidNumberInputKeypressEnter = () => {
  calleeDidNumber.value = calleeDidNumberInputValue.value;
};

const optionsFilter = (option: User) => {
  return callers.value.some(e => e.id === option.id);
};

const fetchCallers = async () => {
  try {
    const api = new ZoomPhoneResultApiService();
    const { data } = await api.getZoomPhoneResultsCallers();
    callers.value = data || [];
    selectUserInputRenderKey.value++;
  } catch (error) {
    callers.value = [];
    console.error(error);
  }
};

const setStartedAtRangeDefault = () => {
  if (!!startedAtFrom.value || !!startedAtTo.value) return;
  startedAtRange.value = [startOf(subtract(new Date(), 7, 'day'), 'day'), endOf(new Date(), 'day')];
};

const setCallerUserDefault = () => {
  if (callerUserId.value) return;
  if (callers.value.some(e => e.id === currentUser.value?.id)) {
    callerUserId.value = currentUser.value?.id?.toString();
  } else {
    callerUserId.value = 'all';
  }
};

onMounted(async () => {
  await fetchCallers();
  setStartedAtRangeDefault();
  setCallerUserDefault();
});
</script>

<style lang="scss" scoped>
.row {
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  gap: $basespace-100;
  padding: $basespace-200 $basespace-600;
}

.caller-user-select {
  width: 176px;
}

.callee-did-number-input {
  width: 300px;
}

.callee-did-number-input-icon {
  :deep(.b-icon) {
    color: $textcolor-light;
  }
}
</style>
